import { ID3 } from 'library/ts/enums/metadata/ID3';
import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { WindowEvent } from 'library/ts/ui/events/WindowEvent';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { PlayerType } from 'tag/ts/tag/model/type/PlayerType';
import { PlayerAdapterType } from 'tag/ts/tag/player/enums/PlayerAdapterType';
import { PlayerProcessor } from 'tag/ts/tag/player/PlayerProcessor';
import { STVPlayerProcessor } from 'tag/ts/tag/player/stv/STVPlayerProcessor';
export class PlayerManager extends AbstractTagClass {
    #playerAdapter;
    #stvPlayerProcessor;
    #playerProcessor;
    #resource;
    #contentType = undefined;
    #autoplay;
    #logLoading;
    #contentTime = NaN;
    #streamTime = NaN;
    #playbackTime = NaN;
    #liveStreamUtcTime = NaN;
    #liveStreamUtcDuration = NaN;
    #liveStreamUtcStart = NaN;
    constructor(argument) {
        super({
            ...argument,
            name: 'PlayerManager',
            onPluginsPhaseDone: () => {
                this?.logger?.log('onPluginsPhaseDone');
            }
        });
        this.#logLoading = LogService.create({ noTimestamp: true });
    }
    get playerAdapter() {
        return this.#playerAdapter;
    }
    get player() {
        return this.#playerAdapter?.player;
    }
    get contentType() {
        return this.#contentType;
    }
    get autoplay() {
        return this.#autoplay;
    }
    get currentResource() {
        return this.#resource;
    }
    get resourceConfig() {
        return new Promise((resolve, reject) => {
            this.playerAdapter.resourceConfig
                .then((data) => {
                const resourceConfig = data?.resource;
                this.logger?.log('get resourceConfig:', 'resolving base resourceConfig:', resourceConfig);
                resolve(resourceConfig);
            })
                .catch((error) => {
                this.logger?.warn('get resourceConfig:', 'failed to get base resourceConfig from playerAdapter:', error);
                reject({ msg: 'playerAdapter.resourceConfig failed', success: false, error: error });
            });
        });
    }
    get currentTime() {
        const output = {
            content: this.#contentTime,
            stream: this.#streamTime,
            contentTime: this.#contentTime,
            streamTime: this.#streamTime,
            playbackTime: this.#playbackTime,
            liveStreamUtcTime: this.#liveStreamUtcTime,
            liveStreamUtcDuration: this.#liveStreamUtcDuration,
            liveStreamUtcStart: this.#liveStreamUtcStart
        };
        this?.logger?.log('currentTime:', output);
        return output;
    }
    initialize = () => {
        return new Promise((resolve, reject) => {
            this.tagEventsHandler(this.#onTagEvent, [
                TagEvent.ON_API_RESET_TAG_REQUEST,
                TagEvent.ON_CREATE_PLAYER_SUCCESS,
                TagEvent.ON_REQUEST_RESOURCE_LOAD
            ]);
            this.#playerAdapter = new window.SmartTag.playerAdapters[PlayerAdapterType.AVIA]({ tag: this.tag });
            this.#playerAdapter.initialize().then((data) => {
                this?.logger?.log('initialize:', data);
                this.#setupWindowBeforeUnloadHandler();
                switch (this.params.SYSTEM.embedType) {
                    case EmbedType.DESKTOP:
                    case EmbedType.MOBILE_WEB:
                        this.#playerProcessor = new PlayerProcessor({ tag: this?.tag });
                        break;
                    case EmbedType.HTML5:
                        this.#stvPlayerProcessor = new STVPlayerProcessor({ tag: this?.tag });
                }
                resolve({ msg: 'Done', success: true, result: this });
            });
        });
    };
    dispatchResourceLoadRequest = (resourceConfig = undefined, tag = this.tag, target = undefined, targetName = undefined) => {
        this?.logger?.log('dispatchResourceLoadRequest:', 'targetName:', targetName, 'resource:', resourceConfig);
        return new Promise((resolve, reject) => {
            try {
                this.dispatchTagEvent(new TagEventVO(TagEvent.ON_REQUEST_RESOURCE_LOAD, tag, this.utils.Type.isEmpty(resourceConfig) ? undefined : { resourceConfig: resourceConfig }, target, targetName));
                resolve({ success: true });
            }
            catch (error) {
                reject({ success: false, error });
            }
        });
    };
    onPlayerAdapterEvent = (tagEvent) => {
        const playerAdapterEvent = tagEvent.data.playerAdapterEvent;
        const playerDestructionCallback = (success, result) => {
            this?.logger?.log('onPlayerAdapterEvent:', 'player removed.:', success, result);
        };
        const resetPlaybackProperties = () => {
            this.#contentTime = NaN;
            this.#streamTime = NaN;
            this.#playbackTime = NaN;
            this.#liveStreamUtcStart = NaN;
            this.#liveStreamUtcTime = NaN;
            this.#liveStreamUtcDuration = NaN;
        };
        const setPlaybackProperties = () => {
            this.#contentTime = this?.player?.contentTime;
            this.#streamTime = this?.player?.streamTime;
            this.#playbackTime = this?.player?.playbackTime;
            this.#liveStreamUtcStart = this?.player?.liveStreamUtcStart;
            this.#liveStreamUtcTime = this?.player?.liveStreamUtcTime;
            this.#liveStreamUtcDuration = this?.player?.liveStreamUtcDuration;
        };
        switch (playerAdapterEvent.type) {
            case PlayerAdapterEvent.ERROR:
                if (!this.utils.Type.isEmpty(playerAdapterEvent?.detail)) {
                    const errorDetail = playerAdapterEvent?.detail?.error;
                    if (errorDetail?.fatal === true) {
                        this?.logger?.log('onPlayerAdapterEvent:', playerAdapterEvent);
                        this.#requestPlayerDestruction(playerDestructionCallback);
                    }
                }
                break;
            case PlayerAdapterEvent.ACTIVE_STATE_CHANGE:
            case PlayerAdapterEvent.AD_PROGRESS:
            case PlayerAdapterEvent.CLOCK:
            case PlayerAdapterEvent.CONTENT_PROGRESS:
            case PlayerAdapterEvent.LOG_EVENT:
            case PlayerAdapterEvent.METRIC:
                break;
            default:
                this?.logger?.log('onPlayerAdapterEvent:', playerAdapterEvent);
        }
        if (playerAdapterEvent.type !== PlayerAdapterEvent.RESOURCE_END) {
            const contentTime = this?.player?.contentTime;
            const streamTime = this?.player?.streamTime;
            if (this.utils.Type.isNumber(contentTime) && contentTime > 0)
                this.#contentTime = contentTime;
            if (this.utils.Type.isNumber(streamTime) && streamTime > 0)
                this.#streamTime = streamTime;
        }
        switch (playerAdapterEvent.type) {
            case PlayerAdapterEvent.RESOURCE_START:
                resetPlaybackProperties();
                break;
            case PlayerAdapterEvent.AD_BREAK_START:
            case PlayerAdapterEvent.AD_START:
            case PlayerAdapterEvent.AD_PROGRESS:
                this.#setContentType(ContentType.AD);
                break;
            case PlayerAdapterEvent.CONTENT_START:
            case PlayerAdapterEvent.CONTENT_SEGMENT_START:
                this.#setContentType(ContentType.CONTENT);
                break;
            case PlayerAdapterEvent.CONTENT_PROGRESS:
                setPlaybackProperties();
                this.#setContentType(ContentType.CONTENT);
                break;
        }
        this.dispatchTagEvent(tagEvent);
    };
    #setupWindowBeforeUnloadHandler = () => {
        const callback = (success, result) => {
            this?.logger?.log('setupWindowBeforeUnloadHandler:', 'player removed.:', success, result);
        };
        window.addEventListener(WindowEvent.BEFORE_UNLOAD, (event) => {
            if (this.utils.Type.isDefined(this?.player) && !this.utils.Type.isEmpty(this?.player?.resource)) {
                this.playerAdapter.requestPlayerStop().then((data) => {
                    this.#requestPlayerDestruction(callback);
                });
            }
        });
    };
    #setContentType = (contentType) => {
        this.#contentType = contentType;
    };
    #addOwnerIDs = (resource) => {
        const addId3TagOwners = (resource) => {
            if (resource?.playback?.id3OwnerIds?.includes('*') === false) {
                resource.playback.id3OwnerIds.push('*');
            }
            if (resource?.playback?.id3OwnerIds?.includes(ID3.NAME) === false) {
                resource.playback.id3OwnerIds.push(ID3.NAME);
            }
            if (this.utils.Type.isFunction(this?.tag?.model?.contentUtilities?.isLinearAdTrackable)
                &&
                    this.tag.model.contentUtilities.isLinearAdTrackable()
                &&
                    resource?.playback?.id3OwnerIds?.includes(ID3.LINEAR_AD_TRACKING) === false) {
                resource.playback.id3OwnerIds.push(ID3.LINEAR_AD_TRACKING);
            }
            return resource;
        };
        if (this.utils.Type.isArray(resource?.playback?.id3OwnerIds) === false) {
            resource.playback.id3OwnerIds = [];
        }
        return addId3TagOwners(resource);
    };
    #loadResource = (auxResourceConfig = undefined) => {
        this?.logger?.log('#loadResource', 'auxResourceConfig:', auxResourceConfig, 'player:', this.player);
        if (!this.utils.Type.isUndefined(this.player) && this.player.isReady === true) {
            const executeResourceLoad = () => {
                switch (this.utils.Type.isDefined(this.#resource)) {
                    case true:
                        if (this.capabilities.isDesktop === true && this.utils.Type.isFunction(this.tag?.view?.loadingAnimationManager?.show)) {
                            this.tag.view.loadingAnimationManager.show();
                        }
                        this.tag.controller.loadDependencies(false).then((data) => {
                            this.#logLoading.log('>>>>>>>>>>>>>>>', '\nSmartTag:', '\nTag:', this.uuid, this.name, 'loadResource:', '\nparams:', this.params, '\nCONTENT.assetURL:', this.params.CONTENT.assetURL, '\napiMetadata.streamingUrl:', this?.apiMetadata?.streamingUrl, '\npid:', this.params.CONTENT.pid, '\nresource:', this.#resource, '\nresource.location.mediaUrl:', this.#resource.location.mediaUrl, '\nad setup:', this.tag?.model?.adUtilities?.adSetupInfo(), '\nimports:', data, '\n>>>>>>>>>>>>>>>');
                            if (!this.utils.Type.isEmpty(this?.player)) {
                                this?.player?.loadResource(this.#resource).then((data) => {
                                    this?.logger?.log('#loadResource success:', data);
                                }).catch((error) => {
                                    this?.logger?.warn('#loadResource failure:', error);
                                });
                            }
                        }).catch((error) => {
                            this?.logger?.warn('loadResource:', 'Failed to load dependencies. Stop execution!');
                            this?.logger?.log(this.name, 'loadResource', 'executeResourceLoad', 'error:', error);
                            this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_FATAL_ERROR, this.tag, {
                                fatalError: {
                                    code: TagErrorCode.DEPENDENCY_LOAD_FAILURE,
                                    msg: {
                                        original: undefined,
                                        custom: this.tag.errorManager.createErrorMessageByCode(TagErrorCode.DEPENDENCY_LOAD_FAILURE)
                                    },
                                    error: error
                                }
                            }));
                        });
                        break;
                    case false:
                        this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_FATAL_ERROR, this.tag, {
                            fatalError: {
                                code: TagErrorCode.RCO_UNDEFINED,
                                msg: {
                                    original: undefined,
                                    custom: this.tag.errorManager.createErrorMessageByCode(TagErrorCode.RCO_UNDEFINED)
                                },
                                error: { resource: this.#resource }
                            }
                        }));
                        break;
                }
            };
            const onBeforeResourceLoad = () => {
                return new Promise((resolve, reject) => {
                    this.dispatchTagEvent(this.getTagEventVO(TagEvent.ON_BEFORE_RESOURCE_LOAD, this.tag, {
                        resourceConfig: this.#resource
                    }));
                    this.utils.Delay.delay(this.params.SYSTEM.embedType === EmbedType.DESKTOP ? 50 : undefined).then(() => {
                        resolve({ msg: 'Done' });
                    });
                });
            };
            if (!this.utils.Type.isEmpty(auxResourceConfig?.location?.mediaUrl)) {
                this.#resource = this.#addOwnerIDs(auxResourceConfig);
                onBeforeResourceLoad().then(() => {
                    executeResourceLoad();
                });
            }
            else {
                this.resourceConfig.then((resourceConfig) => {
                    this.#resource = this.#addOwnerIDs(resourceConfig);
                    onBeforeResourceLoad().then(() => {
                        executeResourceLoad();
                    });
                });
            }
        }
    };
    #onCreateVideoPlayerSuccess = () => {
        this?.logger?.log('PlayerManager.ts > onCreateVideoPlayerSuccess');
        const doneProcessing = () => {
            this?.logger?.log('onCreateVideoPlayerSuccess', 'player type:', this?.tag?.view?.playerType);
            switch (this?.tag?.view?.playerType) {
                case PlayerType.FULL:
                    this.dispatchResourceLoadRequest(undefined, this.tag, this.tag, this.name).then();
                    break;
                case PlayerType.CHROMELESS:
                    if (this?.params?.SKIN?.enabled === false) {
                        this.dispatchResourceLoadRequest(undefined, this.tag, this.tag, this.name).then();
                    }
                    break;
            }
        };
        const shouldDetectAutoplay = this.utils.Type.isEmpty(window.SmartTag.autoplay)
            &&
                this.runtimeVO?.readyCallback?.detectAutoplay === true;
        switch (shouldDetectAutoplay) {
            case false:
                doneProcessing();
                break;
            case true:
                if (this.autoplay === undefined) {
                    this.#playerAdapter.getAutoplayCapabilities().then((data) => {
                        this.#autoplay = data.autoplayInfo;
                        doneProcessing();
                    });
                }
                else {
                    doneProcessing();
                }
                break;
        }
    };
    #requestPlayerDestruction = (callback) => {
        this?.logger?.log('requestPlayerDestruction:', 'dispatch:', TagEvent.ON_DESTROY_PLAYER_REQUEST);
        this.tag.dispatchTagEvent(this.tag.getTagEventVO(TagEvent.ON_DESTROY_PLAYER_REQUEST, this.tag, {
            eventCallback: callback
        }, this, this?.name));
    };
    #resetTag = (event) => {
        const resolveEventPromise = (promiseResolve = undefined, promiseReject = undefined, message = undefined) => {
            let methodToExecute = undefined;
            if (this.utils.Type.isFunction(promiseResolve)) {
                methodToExecute = promiseResolve;
            }
            if (!this.utils.Type.isFunction(methodToExecute) && this.utils.Type.isFunction(promiseResolve)) {
                methodToExecute = promiseReject;
            }
            if (this.utils.Type.isFunction(methodToExecute)) {
                this.utils.Delay.delay().then(() => {
                    methodToExecute({ msg: message });
                });
            }
            else {
                this?.logger?.warn(TagLoggerString.ON_TAG_EVENT, event?.type, event?.data, TagLoggerString.RESET_TAG, 'promiseResolve and promiseReject are not a function');
            }
        };
        const playerStopRequestCallback = (success, result) => {
            this?.logger?.log(TagLoggerString.RESET_TAG, 'playerStopRequestCallback:', 'avia player was stopped:', success);
            switch (success) {
                case true:
                    this?.logger?.log(TagLoggerString.RESET_TAG, 'playerStopRequestCallback:', 'call requestPlayerDestruction');
                    this.#requestPlayerDestruction(playerDestroyRequestCallback);
                    break;
                case false:
                    this?.logger?.warn(TagLoggerString.ON_TAG_EVENT, event?.type, event?.data, 'player stop  Error:', result);
                    break;
            }
        };
        const playerDestroyRequestCallback = (success, result) => {
            this?.logger?.log('resetTag:', TagLoggerString.PLAYER_DESTROY_REQUEST_CALLBACK, 'avia player destroyed:', success);
            switch (success) {
                case true:
                    if (this.utils.Type.isElement(this?.tag?.view?.smartTagElement)) {
                        this.utils.DOM.recreateNode(this.tag.view.smartTagElement, true).then(() => {
                            this.utils.DOM.removeElement(this?.tag?.view?.smartTagElement).then((data) => {
                                this?.logger?.log(TagLoggerString.RESET_TAG, TagLoggerString.PLAYER_DESTROY_REQUEST_CALLBACK, 'removed + destroyed Tag content DOM element');
                                data.element = undefined;
                                delete data?.element;
                                this.tag.eventDispatcher.clear().then((data) => {
                                    this?.logger?.log(TagLoggerString.RESET_TAG, TagLoggerString.PLAYER_DESTROY_REQUEST_CALLBACK, 'unsubscribed from all Tag events');
                                    resolveEventPromise(event?.data?.promiseResolve, undefined, 'Tag reset complete!');
                                });
                            });
                        });
                    }
                    break;
                case false:
                    this?.logger?.warn(TagLoggerString.ON_TAG_EVENT, event?.type, event?.data, 'player.remove Error');
                    break;
            }
        };
        if (this.utils.Type.isDefined(this?.player) && this.utils.Type.isFunction(this?.player?.removePlayerEventListener)) {
            this?.logger?.log(TagLoggerString.RESET_TAG, 'request removal of player event listener');
            this.player.removePlayerEventListener().then((data) => {
                if (!this.utils.Type.isEmpty(this?.player?.resource)) {
                    this?.logger?.log(TagLoggerString.RESET_TAG, 'avia + resource both exist:', 'dispatch:', TagEvent.ON_TAG_PLAYER_STOP_REQUEST);
                    this?.logger?.log(TagLoggerString.RESET_TAG, 'player stop request');
                    this.tag.dispatchTagEvent(this.tag.getTagEventVO(TagEvent.ON_TAG_PLAYER_STOP_REQUEST, this.tag, {
                        eventCallback: playerStopRequestCallback
                    }, this, this.name));
                }
                else {
                    this?.logger?.log(TagLoggerString.RESET_TAG, 'resource:', this?.player?.resource);
                    playerStopRequestCallback(true, undefined);
                }
            });
        }
    };
    #onTagEvent = (event) => {
        switch (event?.type) {
            case TagEvent.ON_CREATE_PLAYER_SUCCESS:
                this?.logger?.log(TagLoggerString.ON_TAG_EVENT, event?.type, event?.data);
                this.#onCreateVideoPlayerSuccess();
                break;
            case TagEvent.ON_REQUEST_RESOURCE_LOAD:
                this?.logger?.log(TagLoggerString.ON_TAG_EVENT, event?.type, event?.data);
                this.#loadResource(event?.data?.resourceConfig);
                break;
            case TagEvent.ON_API_RESET_TAG_REQUEST:
                this?.logger?.log(TagLoggerString.ON_TAG_EVENT, TagEvent.ON_API_RESET_TAG_REQUEST);
                this.#resetTag(event);
                break;
        }
    };
}
